import { useState, useMemo } from "react";
import { Plus } from "lucide-react";
import {
  usePatientAllergies,
  useAllergyTypes,
  useDeletePatientAllergy,
} from "@/hooks/useConsultation";
import { useToast } from "@/hooks/use-toast";
import { AddAllergyDialog } from "@/components/consultation/dialogs/AddAllergyDialog";
import { EmptyState } from "@/components/consultation/EmptyState";
import { DismissibleChip } from "@/components/consultation/shared/DismissibleChip";
import { Table, TableBody, TableRow, TableCell } from "@/components/ui/table";
import {
  Widget,
  WidgetHeader,
  WidgetContent,
} from "@/components/consultation/shared/Widget";
import type { PatientAllergy } from "@/types/appointments";

interface KnownAllergiesCardProps {
  appointmentId: string;
  isCompleted?: boolean;
}

export const KnownAllergiesCard = ({
  appointmentId,
  isCompleted = false,
}: KnownAllergiesCardProps) => {
  const { data: allergies = [], isLoading } =
    usePatientAllergies(appointmentId);
  const { data: allergyTypes = [] } = useAllergyTypes();
  const deleteAllergy = useDeletePatientAllergy();
  const { toast } = useToast();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [addDialogTypeId, setAddDialogTypeId] = useState<number | null>(null);

  const groupedAllergies = useMemo(() => {
    return allergies.reduce(
      (acc, allergy) => {
        const typeId = allergy.allergy_info.allergy_type.id;
        if (!acc[typeId]) acc[typeId] = [];
        acc[typeId].push(allergy);
        return acc;
      },
      {} as Record<number, PatientAllergy[]>
    );
  }, [allergies]);

  const capitalize = (str: string) =>
    str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

  const handleRemoveAllergy = async (allergyId: string, name: string) => {
    try {
      await deleteAllergy.mutateAsync({ appointmentId, allergyId });
      toast({ title: "Removed", description: `"${name}" removed.` });
    } catch {
      toast({
        title: "Error",
        description: "Failed to remove allergy.",
        variant: "destructive",
      });
    }
  };

  const openAddForType = (typeId: number) => {
    setAddDialogTypeId(typeId);
    setIsAddDialogOpen(true);
  };

  return (
    <>
      <Widget grow className="h-full">
        <WidgetHeader title="Known Allergies" hasInfo />
        <WidgetContent className="flex-1 min-h-0 overflow-y-auto scrollbar-thin">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <span className="z-body text-muted-foreground">Loading...</span>
            </div>
          ) : allergyTypes.length === 0 && allergies.length === 0 ? (
            <EmptyState title="No known allergies" variant="compact" />
          ) : (
            <div className="rounded-lg border border-border/60 overflow-hidden">
              <Table>
                <TableBody>
                  {allergyTypes
                    .filter((type) => type.is_active && !type.is_deleted)
                    .map((type) => {
                      const typeAllergies = groupedAllergies[type.id] || [];
                      const hasAllergies = typeAllergies.length > 0;

                      return (
                        <TableRow
                          key={type.id}
                          className="hover:bg-transparent"
                        >
                          <TableCell className="py-2 px-3 whitespace-nowrap w-0">
                            <span className="z-eyebrow text-muted-foreground">
                              {capitalize(type.name)}
                            </span>
                            {hasAllergies && (
                              <span className="z-hint text-muted-foreground/70 ml-1">
                                ({typeAllergies.length})
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="py-2 px-3">
                            <div className="flex flex-wrap items-center gap-1.5">
                              {hasAllergies ? (
                                <>
                                  {typeAllergies.map((allergy) => (
                                    <DismissibleChip
                                      key={allergy.id}
                                      label={allergy.allergy_info.name}
                                      className="border-warning/30 bg-warning/10 text-warning"
                                      onRemove={
                                        !isCompleted
                                          ? () =>
                                              handleRemoveAllergy(
                                                allergy.id,
                                                allergy.allergy_info.name
                                              )
                                          : undefined
                                      }
                                    />
                                  ))}
                                </>
                              ) : (
                                <span className="z-caption text-muted-foreground/50">
                                  No data yet
                                </span>
                              )}
                              {!isCompleted && (
                                <button
                                  type="button"
                                  onClick={() => openAddForType(type.id)}
                                  className="flex items-center justify-center size-5 shrink-0 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                                >
                                  <Plus className="size-3" />
                                </button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </div>
          )}
        </WidgetContent>
      </Widget>

      <AddAllergyDialog
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        appointmentId={appointmentId}
        defaultTypeId={addDialogTypeId}
      />
    </>
  );
};
